import React, { useEffect, useState } from 'react';
import { loginUser, registerUser } from '../services/authService';

function AuthPage({ onAuthSuccess, initialMode = 'login', onBack, isDark }) {
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isLogin = mode === 'login';

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  const resetMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    resetMessages();

    if (!email || !password || (!isLogin && !name)) {
      setErrorMessage('Please fill all required fields.');
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = isLogin
        ? await loginUser({ email, password })
        : await registerUser({ name, email, password });

      if (!payload.token || !payload.user) {
        setErrorMessage('Authentication response is incomplete.');
        return;
      }

      setSuccessMessage(isLogin ? 'Login successful.' : 'Registration successful.');
      onAuthSuccess(payload);

      setPassword('');
    } catch (error) {
      setErrorMessage(error.message || 'Authentication failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSwitchMode = (nextMode) => {
    setMode(nextMode);
    setErrorMessage('');
    setSuccessMessage('');
  };

  return (
    <section className="fade-in-up flex min-h-[calc(100vh-2rem)] items-center justify-center py-2 md:min-h-[calc(100vh-3rem)] md:py-4">
      <div
        className={`grid w-full max-w-5xl overflow-hidden rounded-[32px] border shadow-2xl lg:grid-cols-2 ${
          isDark ? 'border-slate-700 bg-slate-900/80' : 'border-white bg-white/90'
        }`}
      >
        <aside className="relative overflow-hidden bg-gradient-to-br from-cyan-500 via-blue-600 to-indigo-700 p-6 text-white md:p-8">
          <div className="absolute -right-14 -top-10 h-40 w-40 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-orange-400/30 blur-3xl" />

          <div className="relative flex h-full flex-col justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-cyan-100">Postcard SaaS</p>
              <h2 className="mt-3 text-3xl font-black leading-tight md:text-4xl">Launch Better Direct Mail Campaigns</h2>
              <p className="mt-3 max-w-sm text-sm text-cyan-50/90">
                Professional AI postcard workflow for USA teams. Design, export, and track print orders in one place.
              </p>
            </div>

            <div className="mt-8 space-y-3 text-sm">
              <p className="rounded-xl border border-white/30 bg-white/10 px-3 py-2">AI-ready canvas templates</p>
              <p className="rounded-xl border border-white/30 bg-white/10 px-3 py-2">PNG and PDF export flow</p>
              <p className="rounded-xl border border-white/30 bg-white/10 px-3 py-2">Live order tracking dashboard</p>
            </div>
          </div>
        </aside>

        <div className={`p-6 md:p-8 ${isDark ? 'bg-slate-950/85' : 'bg-white/95'}`}>
          <div className="mb-6 flex items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-500">Secure Access</p>
              <h3 className="mt-1 text-2xl font-black">{isLogin ? 'Welcome Back' : 'Create Your Account'}</h3>
              <p className={`mt-1 text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {isLogin ? 'Sign in to open your dashboard.' : 'Register to start your first postcard campaign.'}
              </p>
            </div>

            <button
              type="button"
              onClick={onBack}
              className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                isDark
                  ? 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              Back
            </button>
          </div>

          <form className="space-y-3" onSubmit={onSubmit}>
            {!isLogin && (
              <label className="grid gap-1 text-sm font-medium">
                Full Name
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className={`rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                    isDark
                      ? 'border-slate-700 bg-slate-900 text-slate-100 focus:ring-cyan-500/40'
                      : 'border-slate-300 bg-white text-slate-900 focus:ring-blue-200'
                  }`}
                  placeholder="Jane Smith"
                  autoComplete="name"
                  required
                />
              </label>
            )}

            <label className="grid gap-1 text-sm font-medium">
              Email
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={`rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                  isDark
                    ? 'border-slate-700 bg-slate-900 text-slate-100 focus:ring-cyan-500/40'
                    : 'border-slate-300 bg-white text-slate-900 focus:ring-blue-200'
                }`}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </label>

            <label className="grid gap-1 text-sm font-medium">
              Password
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className={`rounded-xl border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 ${
                  isDark
                    ? 'border-slate-700 bg-slate-900 text-slate-100 focus:ring-cyan-500/40'
                    : 'border-slate-300 bg-white text-slate-900 focus:ring-blue-200'
                }`}
                placeholder="At least 8 characters"
                autoComplete={isLogin ? 'current-password' : 'new-password'}
                required
              />
            </label>

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 w-full rounded-xl bg-gradient-to-r from-cyan-500 via-blue-600 to-indigo-600 px-4 py-2.5 text-sm font-bold text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? 'Please wait...' : isLogin ? 'Login to Dashboard' : 'Create Account'}
            </button>

            {errorMessage && (
              <p className="text-sm font-medium text-red-600" role="alert">
                {errorMessage}
              </p>
            )}

            {successMessage && !errorMessage && (
              <p className="text-sm font-medium text-emerald-600" role="status">
                {successMessage}
              </p>
            )}
          </form>

          <div className={`mt-5 border-t pt-4 text-sm ${isDark ? 'border-slate-700 text-slate-300' : 'border-slate-200 text-slate-600'}`}>
            {isLogin ? 'New here?' : 'Already have an account?'}{' '}
            <button
              type="button"
              onClick={() => onSwitchMode(isLogin ? 'register' : 'login')}
              className="font-semibold text-cyan-500 hover:text-cyan-600"
            >
              {isLogin ? 'Create account' : 'Login instead'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AuthPage;
